if (!customElements.get('free-gift-progress')) {
  class FreeGiftProgress extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseFloat(this.dataset.threshold || 125) * 100; // Convert to cents
      this.cartUpdateUnsubscriber = null;
    }

    getElements() {
      return {
        progressBar: this.querySelector('.reward-meter__progress-bar'),
        fill: this.querySelector('.reward-meter__fill'),
        headline: this.querySelector('[data-progress-message]'),
        container: this.querySelector('.f-free-gift-progress') || this
      };
    }

    connectedCallback() {
      // Wait a bit for dependencies to be ready
      setTimeout(() => {
        // Initial update
        this.updateProgress();
        
        // Listen to cart updates - use PUB_SUB_EVENTS from global scope or fallback
        const cartUpdateEvent = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
        
        if (!window.FoxThemeEvents) {
          console.error('FreeGiftProgress: FoxThemeEvents not available');
          return;
        }
        
        this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
          cartUpdateEvent,
          async (state) => {
            // Use the state from event if available, otherwise fetch
            if (state && (state.items || state.items_subtotal_price !== undefined)) {
              this.updateProgressFromState(state);
            } else {
              // If state doesn't have cart data, fetch it
              this.updateProgress();
            }
          }
        );
      }, 100);
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
    }

    async updateProgress() {
      try {
        const cart = await window.FoxThemeCartHelpers.getState();
        if (!cart) return;
        this.updateProgressFromState(cart);
      } catch (error) {
        console.error('Error fetching cart state:', error);
      }
    }

    updateProgressFromState(cart) {
      try {
        // Re-query elements in case DOM changed
        const elements = this.getElements();
        
        // Calculate subtotal excluding free gifts and check for free gift in one pass
        let subtotal = cart.items_subtotal_price || cart.total_price || 0;
        let hasFreeGift = false;
        
        if (cart.items) {
          cart.items.forEach(item => {
            if (item.properties && item.properties._free_gift === 'true') {
              hasFreeGift = true;
              subtotal -= item.final_line_price || 0;
            }
          });
        }

        // Check if threshold is met
        const thresholdMet = subtotal >= this.threshold;
        
        // If free gift is in cart, always show as unlocked
        const isUnlocked = hasFreeGift || thresholdMet;
        
        // Calculate progress - if unlocked, always show 100%
        let progress = 0;
        if (isUnlocked) {
          progress = 100;
        } else {
          progress = this.threshold > 0 
            ? Math.min((subtotal / this.threshold) * 100, 100) 
            : 0;
        }
        
        const remaining = Math.max(this.threshold - subtotal, 0);
        
        // Only hide if cart is truly empty (no items at all)
        const hasItems = cart.items && cart.items.length > 0;
        const shouldHide = !hasItems;

        // Update visibility
        if (shouldHide) {
          elements.container?.classList.add('f-free-gift-progress--hidden');
        } else {
          elements.container?.classList.remove('f-free-gift-progress--hidden');
        }

        // Update progress fill
        if (elements.fill) {
          elements.fill.style.transform = `scaleX(${progress / 100})`;
          elements.fill.setAttribute('free-gift', isUnlocked ? 'true' : 'false');
        }

        // Update message
        if (elements.headline) {
          const remainingMoney = this.formatMoney(remaining);
          if (isUnlocked) {
            elements.headline.innerHTML = `You've unlocked your <span class="bold">FREE EARRINGS!</span>`;
          } else {
            elements.headline.innerHTML = `You are only <span class="bold">${remainingMoney}</span> away from <span class="bold">FREE EARRINGS</span>`;
          }
        }
      } catch (error) {
        console.error('Error updating free gift progress:', error);
      }
    }

    formatMoney(cents) {
      // Use the global formatMoney function from global.js
      if (typeof formatMoney === 'function') {
        return formatMoney(cents, window.FoxThemeSettings.money_format);
      }
      // Fallback if formatMoney is not available
      return (cents / 100).toFixed(2);
    }
  }

  customElements.define('free-gift-progress', FreeGiftProgress);
}

